import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { MapPin, Users, Home, Bed, CheckCircle2, Calendar, ArrowRight } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { format, differenceInDays } from 'date-fns';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import AvailabilityCalendar from '../components/AvailabilityCalendar';

const customIcon = new L.Icon({
  iconUrl: '/marker-icon.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [bookedDates, setBookedDates] = useState([]);
  const [checkIn, setCheckIn] = useState(() => {
    const d = searchParams.get('checkIn');
    return d ? new Date(d + 'T00:00:00') : null;
  });
  const [checkOut, setCheckOut] = useState(() => {
    const d = searchParams.get('checkOut');
    return d ? new Date(d + 'T00:00:00') : null;
  });
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchProperty = async () => {
      setLoading(true);
      setBookedDates([]);
      try {
        const response = await axios.get(`${API}/properties/${slug}`);
        if (cancelled) return;
        setProperty(response.data);

        // Fetch booked dates with cache-busting
        try {
          const datesRes = await axios.get(`${API}/bookings/dates/${response.data.id}`, {
            params: { _t: Date.now() }
          });
          if (!cancelled) {
            setBookedDates(datesRes.data.booked_dates || []);
          }
        } catch (err) {
          console.error('Error fetching booked dates:', err);
        }
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProperty();
    return () => { cancelled = true; };
  }, [slug]);

  // Auto-refresh availability every 3 minutes
  useEffect(() => {
    if (!property) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/bookings/dates/${property.id}`, {
          params: { _t: Date.now() }
        });
        setBookedDates(res.data.booked_dates || []);
      } catch (err) {
        // silent — don't disrupt the page on poll failure
      }
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [property]);

  // Estimate price when dates are selected
  useEffect(() => {
    if (!checkIn || !checkOut || !property) { setPriceEstimate(null); return; }
    setEstimating(true);
    axios.post(`${API}/pricing/calculate`, {
      property_id: property.id,
      check_in_date: format(checkIn, 'yyyy-MM-dd'),
      check_out_date: format(checkOut, 'yyyy-MM-dd'),
      num_guests: 2,
    }).then(res => setPriceEstimate(res.data))
      .catch(() => setPriceEstimate(null))
      .finally(() => setEstimating(false));
  }, [checkIn, checkOut, property]);

  const disabledDates = bookedDates.map(d => new Date(d));
  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;

  // Check if >70% of next 30 days are booked
  const highDemand = (() => {
    if (!bookedDates.length) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let bookedCount = 0;
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const ds = d.toISOString().split('T')[0];
      if (bookedDates.includes(ds)) bookedCount++;
    }
    return bookedCount / 30 > 0.7;
  })();


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-stone-500 text-lg">Loading property...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-sans mb-4">Property not found</h2>
          <button
            onClick={() => navigate('/properties')}
            className="text-primary hover:underline"
          >
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 min-h-screen">
      <Helmet>
        <title>{property.name} — InFox Suites</title>
        <meta name="description" content={`${property.name} — ${property.location}. ${i18n.language === 'el' ? `Από €${property.base_price_per_night}/βράδυ. Κρατήστε απευθείας.` : `From €${property.base_price_per_night}/night. Book directly.`}`} />
        <meta property="og:title" content={`${property.name} — InFox Suites`} />
        <meta property="og:description" content={`${property.description?.substring(0, 150) || property.name} — ${property.location}`} />
        <meta property="og:image" content={property.images?.[0]} />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Image Gallery */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="aspect-[4/3] overflow-hidden rounded-sm">
              <img
                src={property.images[selectedImage]}
                alt={property.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {property.images.slice(0, 4).map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square overflow-hidden rounded-sm ${
                    selectedImage === index ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <img
                    src={image}
                    alt={`${property.name} - ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Property Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-sans font-semibold mb-4">
                {property.name}
              </h1>
              <div className="flex items-center text-stone-600 mb-6">
                <MapPin className="w-5 h-5 mr-2" />
                <span>{property.location}</span>
              </div>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-sm border border-stone-200">
                <Users className="w-6 h-6 text-primary mb-2" />
                <p className="text-sm text-stone-600">{t('property.guests')}</p>
                <p className="text-lg font-semibold">{property.max_guests}</p>
              </div>
              <div className="bg-white p-4 rounded-sm border border-stone-200">
                <Bed className="w-6 h-6 text-primary mb-2" />
                <p className="text-sm text-stone-600">{t('property.bedrooms')}</p>
                <p className="text-lg font-semibold">{property.bedrooms}</p>
              </div>
              <div className="bg-white p-4 rounded-sm border border-stone-200">
                <Home className="w-6 h-6 text-primary mb-2" />
                <p className="text-sm text-stone-600">{t('property.size')}</p>
                <p className="text-lg font-semibold">{property.size_sqm}m²</p>
              </div>
              <div className="bg-white p-4 rounded-sm border border-stone-200">
                <Calendar className="w-6 h-6 text-primary mb-2" />
                <p className="text-sm text-stone-600">{t('property.minimumStay')}</p>
                <p className="text-lg font-semibold">{property.minimum_stay_days} {t('property.nights')}</p>
              </div>
            </div>

            {/* Amenities */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <h2 className="text-2xl font-sans font-semibold mb-6">{t('property.amenities')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {property.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-700 font-light">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Unique Features */}
            {property.unique_features && property.unique_features.length > 0 && (
              <div className="bg-primary/5 p-6 rounded-sm border border-primary/20">
                <h2 className="text-2xl font-sans font-semibold mb-4">Highlights</h2>
                <ul className="space-y-2">
                  {property.unique_features.map((feature, index) => (
                    <li key={index} className="text-stone-700 font-light flex items-start">
                      <span className="text-primary mr-2">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Location Map */}
            <div className="bg-white p-6 rounded-sm border border-stone-200">
              <h2 className="text-2xl font-sans font-semibold mb-4">{t('property.location')}</h2>
              <div className="aspect-video rounded-sm overflow-hidden">
                <MapContainer
                  center={[property.latitude, property.longitude]}
                  zoom={16}
                  scrollWheelZoom={false}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[property.latitude, property.longitude]} icon={customIcon} />
                </MapContainer>
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-stone-600">{property.floor}</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center"
                  data-testid="google-maps-link"
                >
                  <MapPin className="w-4 h-4 mr-1" />
                  Open in Google Maps
                </a>
              </div>
            </div>

            {/* Availability Calendar */}
            <div>
              <h2 className="text-2xl font-sans font-semibold mb-4">Check Availability</h2>
              <AvailabilityCalendar
                bookedDates={bookedDates}
                minStayDays={property.minimum_stay_days}
                onDateSelect={(date) => {
                  // Navigate to booking page with pre-selected date
                  navigate(`/booking/${property.slug}?checkIn=${date.toISOString()}`);
                }}
              />
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-sm border border-stone-200 sticky top-24">
              <div className="mb-4">
                <div className="flex items-baseline mb-1">
                  <span className="text-4xl font-semibold text-primary">
                    €{property.base_price_per_night}
                  </span>
                  <span className="text-stone-500 font-light ml-2">
                    {t('property.perNight')}
                  </span>
                </div>
                <p className="text-xs text-stone-500">Minimum stay: {property.minimum_stay_days} nights</p>
              </div>

              {highDemand && (
                <div className="mb-4 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-sm px-3 py-2" data-testid="high-demand-badge">
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{i18n.language === 'el' ? 'Υψηλή ζήτηση αυτό το μήνα' : 'High demand this month'}</span>
                </div>
              )}

              {/* Date Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  {t('booking.selectDates')}
                </label>
                <CalendarPicker
                  mode="range"
                  selected={checkIn && checkOut ? { from: checkIn, to: checkOut } : checkIn ? { from: checkIn } : undefined}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setCheckIn(range.from);
                      setCheckOut(range.to);
                    } else if (range?.from) {
                      setCheckIn(range.from);
                      setCheckOut(null);
                    } else {
                      setCheckIn(null);
                      setCheckOut(null);
                    }
                  }}
                  disabled={[{ before: new Date() }, ...disabledDates.map(d => d)]}
                  numberOfMonths={1}
                  className="rounded-sm border border-stone-200"
                  data-testid="booking-calendar"
                />
              </div>

              {/* Price Estimate */}
              {nights > 0 && (
                <div className="mb-4 p-4 bg-stone-50 rounded-sm border border-stone-200" data-testid="price-estimate">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-stone-600">{format(checkIn, 'dd MMM')} → {format(checkOut, 'dd MMM')}</span>
                    <span className="font-medium">{nights} {nights === 1 ? 'night' : 'nights'}</span>
                  </div>
                  {estimating ? (
                    <p className="text-sm text-stone-500 animate-pulse">Calculating...</p>
                  ) : priceEstimate ? (
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-stone-600">€{property.base_price_per_night} × {nights} nights</span>
                        <span>€{priceEstimate.nightly_total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">{t('property.cleaningFee')}</span>
                        <span>€{priceEstimate.cleaning_fee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-stone-600">{t('property.occupancyTax')}</span>
                        <span>€{priceEstimate.occupancy_tax}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-base pt-2 border-t border-stone-200 mt-2">
                        <span>Total</span>
                        <span className="text-primary">€{priceEstimate.total_price}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <button
                onClick={() => {
                  const params = checkIn && checkOut
                    ? `?checkIn=${format(checkIn, 'yyyy-MM-dd')}&checkOut=${format(checkOut, 'yyyy-MM-dd')}`
                    : '';
                  navigate(`/booking/${property.slug}${params}`);
                }}
                className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-sm transition-colors font-semibold flex items-center justify-center space-x-2"
                data-testid="book-now-button"
              >
                <span>{nights > 0 ? (priceEstimate ? `${t('property.bookNow')} — €${priceEstimate.total_price}` : t('property.bookNow')) : t('property.bookNow')}</span>
                <ArrowRight className="w-5 h-5" />
              </button>

              {/* Why Book Direct */}
              <div className="mt-4 grid grid-cols-2 gap-2" data-testid="trust-badges">
                {[
                  { icon: CheckCircle2, text: i18n.language === 'el' ? 'Καλύτερη τιμή' : 'Best price' },
                  { icon: CheckCircle2, text: i18n.language === 'el' ? 'Χωρίς χρεώσεις' : 'No platform fees' },
                  { icon: CheckCircle2, text: i18n.language === 'el' ? 'Άμεση επικοινωνία' : 'Direct contact' },
                  { icon: CheckCircle2, text: i18n.language === 'el' ? 'Ασφαλής κράτηση' : 'Secure booking' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <item.icon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs text-stone-600">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Pricing Info */}
              <div className="space-y-2 mt-4 pt-4 border-t border-stone-200 text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-600">{t('property.cleaningFee')}</span>
                  <span className="font-semibold">€{property.cleaning_fee}</span>
                </div>
                {property.short_stay_cleaning_fee && (
                  <div className="flex justify-between text-xs text-stone-500">
                    <span>Short stay (&lt;3 nights)</span>
                    <span>€{property.short_stay_cleaning_fee}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-stone-600">{t('property.occupancyTax')}</span>
                  <span className="font-semibold">€{property.occupancy_tax_per_day}/day</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">{t('property.extraPerson')}</span>
                  <span className="font-semibold">€{property.extra_person_fee}/night</span>
                </div>
                <p className="text-xs text-stone-500 italic">
                  (after {property.extra_person_starts_after} guests)
                </p>
              </div>

              {property.available_until && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                  <p className="text-xs text-amber-800">
                    This property will leave our portfolio on {property.available_until}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
